import React, { useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import { Avatar } from "@mui/material";
import nvidiaLogo from "../assets/nvidia-7.svg";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useFundamentalsSelection } from "./FundamentalsSelectionContext";
import Paper from "@mui/material/Paper";
import { red, green } from "@mui/material/colors";

function CustomBreadcrumbs() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const breadcrumbs = [
		<Link
			component={RouterLink}
			variant="body2"
			underline="hover"
			key="1"
			color="inherit"
			to="/"
		>
			Home
		</Link>,
		<Link
			component={RouterLink}
			variant="body2"
			underline="hover"
			key="1"
			color="inherit"
			to="/fundamentals-search"
		>
			Fundamentals Search
		</Link>,
		<Typography
			key="3"
			variant="body2"
			sx={{ color: "text.primary", marginTop: 0.1 }}
		>
			Results
		</Typography>,
	];

	return (
		<Breadcrumbs
			separator="›"
			aria-label="breadcrumb"
			sx={{
				marginTop: isMobile
					? `calc(61px + 0.3rem)`
					: `calc(65px + 0.3rem)`,
				marginLeft: "0.3rem",
			}}
		>
			{breadcrumbs}
		</Breadcrumbs>
	);
}

function FundamentalsSearchButton({ text, onClick, selected }) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isTablet = useMediaQuery(theme.breakpoints.down("md"));
	const isBigTablet = useMediaQuery(theme.breakpoints.down("lg"));

	return (
		<Grid>
			<Button
				size="large"
				variant={selected ? "contained" : "outlined"}
				endIcon={selected ? <RemoveIcon /> : <AddIcon />}
				onClick={onClick}
				sx={{
					textTransform: "none",
					borderRadius: "1.25rem",
					width: isMobile
						? "70vw"
						: isTablet
						? "45vw"
						: isBigTablet
						? "30vw"
						: "20.83vw",
					justifyContent: "space-between",
					// border: '1px dashed grey',
					backgroundColor: selected ? "success.main" : "",
				}}
			>
				{text}
			</Button>
		</Grid>
	);
}

function FundamentalsSearchButtonGroup() {
	const theme = useTheme();
	const isBigScreen = useMediaQuery(theme.breakpoints.down("lg"));
	const isMidScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const navigate = useNavigate(); // Hook for programmatic navigation

	const goToResults = () => {
		window.scrollTo({
			top: isMidScreen ? 300 : 50,
			behavior: "smooth",
		});

		navigate("/fundamentals-search/results");
	};

	// State to track the selected status of each button
	const { selectedButtons, toggleButtonSelection } =
		useFundamentalsSelection();

	// Calculate if any button is selected
	const isAnyButtonSelected = Object.values(selectedButtons).some(
		(selected) => selected
	);

	return (
		<Box
			sx={{
				marginTop: isBigScreen ? "2rem" : "3rem",
				marginLeft: "auto",
				marginRight: "auto",
				maxWidth: isBigScreen ? "88vw" : "87.32vw",
				// border: '1px dashed grey',
			}}
		>
			<Typography
				component="h2"
				variant="h6"
				align="center"
				fontWeight={"bold"}
				sx={{}}
			>
				Fundamentals
			</Typography>
			<Grid
				container
				gap={isMobile ? 1 : 2}
				justifyContent={isBigScreen ? "center" : "space-between"}
				sx={{ marginTop: isMobile ? "1rem" : "1.5rem", width: "100%" }}
			>
				<FundamentalsSearchButton
					text="5YR P/E Ratio < 22.5"
					onClick={() => toggleButtonSelection("peRatio")}
					selected={selectedButtons.peRatio}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="5YR ROIC"
					onClick={() => toggleButtonSelection("roic")}
					selected={selectedButtons.roic}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Revenue Growth 5 Yr"
					onClick={() => toggleButtonSelection("revenueGrowth")}
					selected={selectedButtons.revenueGrowth}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Net Income Growth 5 Yr"
					onClick={() => toggleButtonSelection("netIncomeGrowth")}
					selected={selectedButtons.netIncomeGrowth}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Shares Outstanding"
					onClick={() => toggleButtonSelection("sharesOutstanding")}
					selected={selectedButtons.sharesOutstanding}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="LTL / 5 Yr FCF < 5"
					onClick={() => toggleButtonSelection("ltlToFcf")}
					selected={selectedButtons.ltlToFcf}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Cash Flow Growth 5 Yr"
					onClick={() => toggleButtonSelection("cashFlowGrowth")}
					selected={selectedButtons.cashFlowGrowth}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="5YR Price:FCF < 22.5"
					onClick={() => toggleButtonSelection("priceToFcf")}
					selected={selectedButtons.priceToFcf}
				></FundamentalsSearchButton>
			</Grid>
			<Box
				display="flex"
				justifyContent="center"
				sx={{ marginTop: "1.3rem" }}
			>
				<Button
					startIcon={<SearchIcon />}
					variant="contained"
					onClick={goToResults}
					disabled={!isAnyButtonSelected} // Disable if no button is selected
					sx={{ textTransform: "none", borderRadius: 3 }}
				>
					Search
				</Button>
			</Box>
		</Box>
	);
}

function CompanyCard() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const companyName = "Nvidia Corp";
	const tickerSymbol = "NVDA";
	const exchange = "NAS";
	const logo = nvidiaLogo;
	const stockPrice = 140.52; // Example price
	const pricePercentageChange = -0.72;
	const currency = "$";

	return (
		<Grid>
			<Paper
				elevation={2}
				display="flex"
				sx={{
					minWidth: isMobile ? "85vw" : "25vw",
					p: 1.5,
					borderRadius: 3,
				}}
			>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
				>
					<Box
						display="flex"
						component="section"
						sx={{ alignItems: "center" }}
					>
						<Avatar
							alt={companyName}
							src={logo}
							sx={{
								width: 56, // Size of the logo
								height: 56,
								//   borderRadius: '50%',
								marginRight: 1.5,
							}}
							imgProps={{
								style: {
									objectFit: "contain", // Ensure the image fits inside
									width: "90%",
									height: "90%",
								},
							}}
						/>
						<Box
							sx={{
								alignItems: "center",
								mr: isMobile ? "" : "5rem",
							}}
						>
							<Typography
								variant="body1"
								component="div"
								fontWeight={"bold"}
							>
								{companyName}
							</Typography>
							<Box
								display={"flex"}
								sx={{ alignItems: "center", mt: 0.75 }}
							>
								<Typography variant="body1" fontWeight={"bold"}>
									{tickerSymbol}
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									&nbsp;&nbsp;{exchange}
								</Typography>
							</Box>
						</Box>
					</Box>
					<Box>
						<Typography
							variant="body1"
							component="div"
							align="right"
							fontWeight="bold"
						>
							{currency}
							{stockPrice.toFixed(2)}
						</Typography>
						<Typography
							variant="body2"
							component="div"
							sx={{
								color:
									pricePercentageChange > 0
										? green[600]
										: pricePercentageChange === 0
										? "inherit"
										: red[600],
								fontWeight: "bold",
								mt: 0.75,
							}}
						>
							{pricePercentageChange > 0
								? "+"
								: pricePercentageChange === 0
								? ""
								: "-"}
							{currency}
							{Math.abs(
								stockPrice * (pricePercentageChange / 100)
							).toFixed(2)}{" "}
							({pricePercentageChange}%)
						</Typography>
					</Box>
				</Box>
			</Paper>
		</Grid>
	);
}

function Results() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	return (
		<Box
			sx={{
				marginLeft: isMobile ? "auto" : "3rem",
				marginRight: isMobile ? "auto" : "3rem",
				// margin: 'auto',
				marginTop: isMobile ? "3.5rem" : "7rem",
				marginBottom: "7rem",
				justifyContent: "center",
			}}
		>
			<Grid
				container
				rowSpacing={isMobile ? 2 : 3}
				columnSpacing={5}
				justifyContent={"center"}
			>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
				<CompanyCard></CompanyCard>
			</Grid>
		</Box>
	);
}

export default function FundamentalsSearchResult() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	useEffect(() => {
		// Scroll to 300px from the top of the page when the component mounts
		window.scrollTo({
			top: isMobile ? 300 : 50,
			behavior: "smooth",
		});
	}, [isMobile]);

	return (
		<>
			<CustomBreadcrumbs></CustomBreadcrumbs>
			<FundamentalsSearchButtonGroup></FundamentalsSearchButtonGroup>
			<Results></Results>
		</>
	);
}
